// Knowledge Graph Mock Data
// This file contains sample data for the Knowledge Graph functionality
// Organized for easy migration to Supabase backend later

export const knowledgeGraphSamples = [
  {
    id: 'weaving_story',
    title: 'The Little Weaver and the Whispering Woods',
    content: `Once upon a time, in a land dusted with starlight and whispered secrets, lived a young girl named Elara. She wasn't a princess or a knight, but a simple weaver, her fingers nimble and her heart kind. She lived with her grandmother in a small cottage on the edge of the Whispering Woods, a place rumored to hold both magic and mystery.

Elara's days were filled with the rhythmic clack of her loom, weaving colorful threads into tapestries that depicted the world around her. Her grandmother, a wise woman with eyes that held the wisdom of the woods, taught her the craft, and also spoke of the importance of kindness. "The woods," she would say, "listen to those who are kind, and their secrets will be revealed."

One evening, as Elara worked late into the night, she heard a soft melody drifting from the woods. It was unlike any sound she had ever heard - part wind, part song, and entirely magical. The next morning, she found a small, silver thread at her doorstep, shimmering with an otherworldly light.

Her grandmother smiled knowingly. "The woods have chosen you," she said. "They see the kindness in your heart and the beauty in your work." From that day forward, Elara's tapestries began to tell stories not just of the world she saw, but of the magic that lay hidden in the Whispering Woods.`,
    category: 'fantasy',
    difficulty: 'intermediate',
    themes: ['magic', 'craftsmanship', 'family', 'nature']
  },
  {
    id: 'moon_rabbit_story',
    title: 'The Moon and the Rabbit',
    content: `This is a story about the moon and a rabbit, about promise and sacrifice. A long, long time ago, on a distant planet, lived a pure white rabbit. Like other rabbits, he ran in the fields every day, enjoying the sunshine and freedom. However, he had an unusual heart, full of curiosity and reverence for the moon.

Every night, he would look up at the bright moon, his heart full of longing for the moon. He wanted to know what was on the moon, how much he longed to be close to the moon. One day, he finally made up his mind to climb up to the moon.

The journey was not easy. He had to cross rivers, climb mountains, and face many challenges. But his determination never wavered. Along the way, he met an old man who lived in a thatched hut in the forest. The old man's wife was sick, and the rabbit helped them by bringing medicine from the mountain herbs.

In return, the old man gave the rabbit a special blessing that would help him reach the moon. The rabbit continued his journey, and finally, after many days and nights, he reached the moon. There, he discovered that the moon was not just a bright light in the sky, but a place of pure magic and wonder.

The rabbit's sacrifice and determination taught everyone that dreams are worth pursuing, no matter how impossible they may seem.`,
    category: 'fable',
    difficulty: 'beginner',
    themes: ['determination', 'sacrifice', 'dreams', 'helping others']
  },
  {
    id: 'crystal_city_story',
    title: 'The Crystal City of Light',
    content: `In the heart of a vast desert, where the sun blazed like a furnace and the sand stretched endlessly to the horizon, there stood a magnificent city made entirely of crystal. The Crystal City of Light, as it was known, was home to a people who had mastered the art of harnessing sunlight to power their civilization.

The city's ruler, Queen Seraphina, was a wise and just leader who had inherited the throne from her mother, the legendary Crystal Weaver. The queen's advisor, Master Zephyr, was a brilliant inventor who had created the city's most important invention - the Solar Collector, a massive crystal structure that captured and stored the sun's energy.

However, the city faced a grave threat. The Dark Sands, a mysterious force that consumed light and left only darkness in its wake, were slowly approaching the city. The people of the Crystal City knew that if the Dark Sands reached them, their beautiful city would be lost forever.

Princess Aurora, the queen's daughter, was determined to save her city. She studied ancient texts in the Great Library and discovered that the only way to stop the Dark Sands was to find the Heart Crystal, a legendary gem that could purify darkness and restore light.

With the help of her loyal friend, the young inventor Spark, and the wise old sage, Master Luminary, Princess Aurora embarked on a dangerous journey to find the Heart Crystal. They traveled through treacherous mountains, crossed raging rivers, and faced many challenges along the way.

Finally, after many trials and tribulations, they found the Heart Crystal hidden in a cave deep within the mountains. With the crystal's power, they were able to drive back the Dark Sands and save the Crystal City of Light.

The story teaches us that even in the darkest times, hope and determination can light the way to a brighter future.`,
    category: 'adventure',
    difficulty: 'advanced',
    themes: ['leadership', 'technology', 'courage', 'hope', 'friendship']
  }
  ,
  {
    id: 'city_detective_case',
    title: 'Shadows in Central: A Detective Tale',
    content: `Detective Lin arrived in Central just as the rain began. A stolen ledger linked to a smuggling ring had vanished from a lawyer's office. Lin interviewed the night guard, a nervous intern, and a rival attorney. The pattern of receipts led him to a forgotten pier. There, a courier tried to burn the evidence, but the tide soaked the pages. By dawn, Lin pieced together the network and delivered the case to prosecutors.`,
    category: 'detective',
    difficulty: 'intermediate',
    themes: ['investigation', 'justice', 'city']
  },
  {
    id: 'quiet_romance',
    title: 'Letters in the Library',
    content: `Mei worked evenings in a small library. Each week, a stranger left short notes between poetry books: a line of Rilke, a question about memory, a sketch of the moon. Mei replied with pressed flowers and quiet answers. One rainy night, the power failed, and two flashlights found each other in the stacks. The notes had built a language; the library kept their promise.`,
    category: 'romance',
    difficulty: 'beginner',
    themes: ['love', 'letters', 'serendipity']
  },
  {
    id: 'school_friendship',
    title: 'The Bridge Team',
    content: `Four classmates entered the STEM fair with a model bridge. The first prototype collapsed; the second twisted. They argued over angles, apologized over snacks, and kept building. On the final day, the bridge held ten kilograms without bending. They did not win first place, but they crossed something harder together.`,
    category: 'narrative',
    difficulty: 'beginner',
    themes: ['teamwork', 'perseverance', 'learning']
  },
  {
    id: 'myth_seed',
    title: 'The Seed that Remembered',
    content: `In a dry valley, a farmer planted a seed that knew stories. Each night it whispered the memory of rain, rivers, and birds. The farmer watered it with patience, and the village listened. When the seed became a tree, its shade taught children the history of the wind.`,
    category: 'myth',
    difficulty: 'intermediate',
    themes: ['memory', 'nature', 'patience']
  },
  {
    id: 'sci_port',
    title: 'Harbor of Satellites',
    content: `A coastal town refitted its old lighthouse with a satellite uplink. Teenagers mapped fishing grounds with drones; elders tracked storms with open data. On launch night, the beam reached the new cube-sat, and the whole harbor cheered. Tradition and telemetry shared the same horizon.`,
    category: 'science',
    difficulty: 'intermediate',
    themes: ['technology', 'community', 'sea']
  },
  {
    id: 'sports_redemption',
    title: 'Ninth Inning Lesson',
    content: `Down by two runs, Aya stepped to the plate. She had struck out twice already. Coach whispered, 'Breathe through the count.' Aya watched the spin, let two balls pass, and then sent the next pitch over left field. The team tied the game, but what stayed with her was the quiet between breaths.`,
    category: 'sports',
    difficulty: 'beginner',
    themes: ['resilience', 'focus', 'growth']
  },
  {
    id: 'historical_glimpse',
    title: 'Paper Lantern Treaty',
    content: `In an old port city, two guilds ended a decade of rivalry by writing their terms on paper lanterns and releasing them together. The wind scattered light across the water, and the crowd learned that peace can be lifted, not just signed.`,
    category: 'historical',
    difficulty: 'intermediate',
    themes: ['reconciliation', 'symbolism']
  }
];

// Mock API responses for each sample story
export const mockApiResponses = {
  weaving_story: {
    entities: {
      characters: [
        {
          id: 'elara',
          name: 'Elara',
          type: 'protagonist',
          description: 'A kind young weaver with nimble fingers and a pure heart',
          importance: 10,
          tags: ['protagonist', 'weaver', 'kind']
        },
        {
          id: 'grandmother',
          name: 'Grandmother',
          type: 'mentor',
          description: 'Wise woman who teaches weaving and speaks of kindness and wood wisdom',
          importance: 8,
          tags: ['mentor', 'wise', 'teacher']
        }
      ],
      places: [
        {
          id: 'cottage',
          name: 'Small Cottage',
          type: 'dwelling',
          description: 'Elara\'s cozy home on the edge of the mysterious woods',
          importance: 6,
          tags: ['home', 'safe']
        },
        {
          id: 'whispering_woods',
          name: 'Whispering Woods',
          type: 'magical_realm',
          description: 'Mysterious forest full of magic, mystery, and secrets',
          importance: 9,
          tags: ['magical', 'mysterious', 'dangerous']
        },
        {
          id: 'starlit_land',
          name: 'Starlit Land',
          type: 'setting',
          description: 'The enchanted land dusted with starlight and whispered secrets',
          importance: 7,
          tags: ['enchanted', 'beautiful']
        }
      ],
      objects: [
        {
          id: 'loom',
          name: 'Weaving Loom',
          type: 'craft_tool',
          description: 'Elara\'s rhythmic tool for creating beautiful tapestries',
          importance: 7,
          tags: ['tool', 'creative']
        },
        {
          id: 'tapestries',
          name: 'Colorful Tapestries',
          type: 'artwork',
          description: 'Beautiful woven art depicting the world around Elara',
          importance: 8,
          tags: ['art', 'beautiful']
        },
        {
          id: 'colorful_threads',
          name: 'Colorful Threads',
          type: 'materials',
          description: 'Vibrant threads used to weave stories and beauty',
          importance: 6,
          tags: ['materials', 'vibrant']
        },
        {
          id: 'kindness',
          name: 'Kindness',
          type: 'virtue',
          description: 'The most important quality that opens woodland secrets',
          importance: 10,
          tags: ['virtue', 'important']
        },
        {
          id: 'wood_wisdom',
          name: 'Wisdom of the Woods',
          type: 'knowledge',
          description: 'Ancient knowledge held in grandmother\'s eyes',
          importance: 9,
          tags: ['knowledge', 'ancient']
        },
        {
          id: 'wood_secrets',
          name: 'Wood Secrets',
          type: 'mystery',
          description: 'Hidden knowledge revealed only to those who are kind',
          importance: 8,
          tags: ['mystery', 'hidden']
        },
        {
          id: 'silver_thread',
          name: 'Silver Thread',
          type: 'artifact',
          description: 'Magical thread from the woods that shimmers with otherworldly light',
          importance: 9,
          tags: ['magical', 'gift']
        }
      ]
    },
    relationships: [
      { from: 'elara', to: 'grandmother', type: 'lives with', strength: 9 },
      { from: 'grandmother', to: 'elara', type: 'teaches craft', strength: 8 },
      { from: 'grandmother', to: 'elara', type: 'teaches kindness', strength: 9 },
      { from: 'elara', to: 'loom', type: 'uses daily', strength: 8 },
      { from: 'elara', to: 'tapestries', type: 'creates', strength: 9 },
      { from: 'colorful_threads', to: 'tapestries', type: 'becomes', strength: 8 },
      { from: 'elara', to: 'kindness', type: 'possesses', strength: 10 },
      { from: 'cottage', to: 'whispering_woods', type: 'borders', strength: 7 },
      { from: 'grandmother', to: 'wood_wisdom', type: 'possesses', strength: 9 },
      { from: 'kindness', to: 'wood_secrets', type: 'unlocks', strength: 10 },
      { from: 'whispering_woods', to: 'wood_secrets', type: 'holds', strength: 9 },
      { from: 'tapestries', to: 'starlit_land', type: 'depicts', strength: 7 },
      { from: 'elara', to: 'cottage', type: 'lives in', strength: 8 },
      { from: 'loom', to: 'cottage', type: 'located in', strength: 6 },
      { from: 'whispering_woods', to: 'starlit_land', type: 'part of', strength: 8 },
      { from: 'whispering_woods', to: 'elara', type: 'chooses', strength: 8 },
      { from: 'silver_thread', to: 'elara', type: 'gifted to', strength: 9 }
    ],
    analysis: {
      total_entities: 9,
      total_relationships: 16,
      main_themes: ['magic', 'craftsmanship', 'family', 'nature', 'kindness'],
      narrative_arc: 'A young weaver discovers her magical connection to the woods through kindness and craftsmanship',
      complexity_score: 8
    }
  },
  city_detective_case: {
    entities: {
      characters: [
        { id: 'lin', name: 'Detective Lin', type: 'protagonist', description: 'Methodical city detective', importance: 10, tags: ['detective'] },
        { id: 'guard', name: 'Night Guard', type: 'supporting_character', description: 'Witness at the office', importance: 6 },
        { id: 'intern', name: 'Nervous Intern', type: 'supporting_character', description: 'Young employee with clues', importance: 6 },
        { id: 'rival', name: 'Rival Attorney', type: 'antagonist', description: 'Potential accomplice', importance: 7 }
      ],
      places: [
        { id: 'central', name: 'Central District', type: 'setting', description: 'Rainy financial center', importance: 6 },
        { id: 'law_office', name: 'Law Office', type: 'landmark', description: 'Site of the theft', importance: 8 },
        { id: 'old_pier', name: 'Forgotten Pier', type: 'landmark', description: 'Meeting point at the harbor', importance: 9 }
      ],
      objects: [
        { id: 'ledger', name: 'Stolen Ledger', type: 'artifact', description: 'Evidence of smuggling ring', importance: 10 },
        { id: 'receipts', name: 'Receipts', type: 'knowledge', description: 'Pattern reveals routes', importance: 8 },
        { id: 'courier', name: 'Courier', type: 'supporting_character', description: 'Tried to burn evidence', importance: 7 }
      ]
    },
    relationships: [
      { from: 'lin', to: 'law_office', type: 'investigates', strength: 9 },
      { from: 'ledger', to: 'smuggling', type: 'links to', strength: 9 },
      { from: 'guard', to: 'law_office', type: 'works at', strength: 7 },
      { from: 'intern', to: 'receipts', type: 'handled', strength: 6 },
      { from: 'lin', to: 'receipts', type: 'analyzes', strength: 8 },
      { from: 'receipts', to: 'old_pier', type: 'leads to', strength: 9 },
      { from: 'courier', to: 'ledger', type: 'tries to burn', strength: 8 },
      { from: 'lin', to: 'courier', type: 'arrests', strength: 8 },
      { from: 'rival', to: 'ledger', type: 'seeks', strength: 6 },
      { from: 'lin', to: 'prosecutors', type: 'delivers case to', strength: 7 }
    ],
    analysis: { total_entities: 10, total_relationships: 10, main_themes: ['investigation','justice'], narrative_arc: 'Detective reconstructs network and closes case', complexity_score: 7 }
  },
  quiet_romance: {
    entities: {
      characters: [
        { id: 'mei', name: 'Mei', type: 'protagonist', description: 'Evening librarian', importance: 9 },
        { id: 'stranger', name: 'Stranger', type: 'supporting_character', description: 'Leaves notes in books', importance: 8 }
      ],
      places: [
        { id: 'library', name: 'Small Library', type: 'setting', description: 'Quiet place of letters', importance: 8 }
      ],
      objects: [
        { id: 'notes', name: 'Paper Notes', type: 'artifact', description: 'Quotes, questions, sketches', importance: 9 },
        { id: 'flowers', name: 'Pressed Flowers', type: 'artifact', description: 'Mei’s replies', importance: 6 },
        { id: 'flashlights', name: 'Two Flashlights', type: 'artifact', description: 'Found each other in blackout', importance: 7 }
      ]
    },
    relationships: [
      { from: 'stranger', to: 'notes', type: 'leaves', strength: 9 },
      { from: 'mei', to: 'flowers', type: 'replies with', strength: 7 },
      { from: 'notes', to: 'library', type: 'hidden in', strength: 8 },
      { from: 'flashlights', to: 'library', type: 'meet in', strength: 7 },
      { from: 'mei', to: 'stranger', type: 'connects with', strength: 8 }
    ],
    analysis: { total_entities: 7, total_relationships: 5, main_themes: ['notes','connection'], narrative_arc: 'Two readers build a language of letters', complexity_score: 6 }
  },
  school_friendship: {
    entities: {
      characters: [
        { id: 'team', name: 'Bridge Team', type: 'protagonist', description: 'Four classmates', importance: 9 },
        { id: 'coach', name: 'Mentor Teacher', type: 'mentor', description: 'Guides with patience', importance: 6 }
      ],
      places: [ { id: 'fair', name: 'STEM Fair', type: 'setting', description: 'Competition hall', importance: 7 } ],
      objects: [
        { id: 'prototype1', name: 'First Prototype', type: 'artifact', description: 'Collapsed design', importance: 6 },
        { id: 'prototype2', name: 'Second Prototype', type: 'artifact', description: 'Twisted design', importance: 6 },
        { id: 'final_bridge', name: 'Final Bridge', type: 'artifact', description: 'Held ten kilograms', importance: 9 }
      ]
    },
    relationships: [
      { from: 'team', to: 'prototype1', type: 'builds', strength: 7 },
      { from: 'team', to: 'prototype2', type: 'rebuilds', strength: 7 },
      { from: 'team', to: 'final_bridge', type: 'completes', strength: 9 },
      { from: 'final_bridge', to: 'fair', type: 'tested at', strength: 7 }
    ],
    analysis: { total_entities: 6, total_relationships: 4, main_themes: ['teamwork','perseverance'], narrative_arc: 'Students iterate and succeed together', complexity_score: 5 }
  },
  myth_seed: {
    entities: {
      characters: [ { id: 'farmer', name: 'Farmer', type: 'protagonist', description: 'Patient caretaker', importance: 8 } ],
      places: [ { id: 'valley', name: 'Dry Valley', type: 'setting', description: 'Place waiting for rain', importance: 7 } ],
      objects: [
        { id: 'seed', name: 'Remembering Seed', type: 'artifact', description: 'Whispers stories of rain', importance: 9 },
        { id: 'tree', name: 'Story Tree', type: 'landmark', description: 'Grows to teach history', importance: 9 }
      ]
    },
    relationships: [
      { from: 'farmer', to: 'seed', type: 'plants', strength: 8 },
      { from: 'seed', to: 'valley', type: 'whispers to', strength: 7 },
      { from: 'seed', to: 'tree', type: 'becomes', strength: 9 }
    ],
    analysis: { total_entities: 4, total_relationships: 3, main_themes: ['memory','nature'], narrative_arc: 'A seed remembers rain and becomes a teaching tree', complexity_score: 5 }
  },
  sci_port: {
    entities: {
      characters: [
        { id: 'teens', name: 'Teen Mappers', type: 'supporting_character', description: 'Operate drones', importance: 6 },
        { id: 'elders', name: 'Elder Forecasters', type: 'supporting_character', description: 'Track storms', importance: 6 }
      ],
      places: [ { id: 'harbor', name: 'Harbor', type: 'setting', description: 'Coastal town', importance: 8 }, { id: 'lighthouse', name: 'Lighthouse', type: 'landmark', description: 'Refitted with uplink', importance: 9 } ],
      objects: [ { id: 'uplink', name: 'Satellite Uplink', type: 'artifact', description: 'Connects to cubesat', importance: 9 }, { id: 'cubesat', name: 'CubeSat', type: 'artifact', description: 'Small satellite link', importance: 8 } ]
    },
    relationships: [
      { from: 'uplink', to: 'cubesat', type: 'connects to', strength: 9 },
      { from: 'teens', to: 'harbor', type: 'map grounds of', strength: 7 },
      { from: 'elders', to: 'harbor', type: 'track storms for', strength: 7 }
    ],
    analysis: { total_entities: 7, total_relationships: 3, main_themes: ['technology','community'], narrative_arc: 'Town unites tradition and telemetry', complexity_score: 5 }
  },
  sports_redemption: {
    entities: {
      characters: [ { id: 'aya', name: 'Aya', type: 'protagonist', description: 'Batter at the plate', importance: 9 }, { id: 'coach', name: 'Coach', type: 'mentor', description: 'Guides breathing', importance: 6 } ],
      places: [ { id: 'field', name: 'Ball Field', type: 'setting', description: 'Late game', importance: 7 } ],
      objects: [ { id: 'pitch', name: 'Spinning Pitch', type: 'mystery', description: 'Crucial ball', importance: 7 } ]
    },
    relationships: [
      { from: 'coach', to: 'aya', type: 'advises', strength: 7 },
      { from: 'aya', to: 'pitch', type: 'hits', strength: 9 },
      { from: 'aya', to: 'field', type: 'ties game at', strength: 7 }
    ],
    analysis: { total_entities: 4, total_relationships: 3, main_themes: ['focus','growth'], narrative_arc: 'Athlete finds calm and delivers', complexity_score: 5 }
  },
  historical_glimpse: {
    entities: {
      characters: [ { id: 'guilds', name: 'Two Guilds', type: 'supporting_character', description: 'Rivals seeking peace', importance: 7 } ],
      places: [ { id: 'port', name: 'Old Port City', type: 'setting', description: 'Harbor for ceremony', importance: 7 } ],
      objects: [ { id: 'lanterns', name: 'Paper Lanterns', type: 'artifact', description: 'Carry written terms', importance: 9 } ]
    },
    relationships: [
      { from: 'guilds', to: 'lanterns', type: 'write on', strength: 8 },
      { from: 'lanterns', to: 'port', type: 'released in', strength: 8 }
    ],
    analysis: { total_entities: 3, total_relationships: 2, main_themes: ['reconciliation','symbol'], narrative_arc: 'Peace lifted with lanterns over water', complexity_score: 4 }
  },
  moon_rabbit_story: {
    entities: {
      characters: [
        {
          id: 'white_rabbit',
          name: 'White Rabbit',
          type: 'protagonist',
          description: 'A pure white rabbit with curiosity and reverence for the moon',
          importance: 10,
          tags: ['protagonist', 'curious', 'determined']
        },
        {
          id: 'old_man',
          name: 'Old Man',
          type: 'supporting_character',
          description: 'A kind old man who lives in a thatched hut and helps the rabbit',
          importance: 7,
          tags: ['helper', 'kind']
        },
        {
          id: 'old_man_wife',
          name: 'Old Man\'s Wife',
          type: 'supporting_character',
          description: 'The old man\'s sick wife who receives help from the rabbit',
          importance: 5,
          tags: ['sick', 'helped']
        }
      ],
      places: [
        {
          id: 'distant_planet',
          name: 'Distant Planet',
          type: 'setting',
          description: 'The faraway world where the story takes place',
          importance: 6,
          tags: ['distant', 'exotic']
        },
        {
          id: 'fields',
          name: 'Fields',
          type: 'landmark',
          description: 'Open fields where the rabbit runs and enjoys freedom',
          importance: 5,
          tags: ['open', 'free']
        },
        {
          id: 'moon',
          name: 'Moon',
          type: 'magical_realm',
          description: 'The bright moon that the rabbit longs to reach',
          importance: 9,
          tags: ['magical', 'desired']
        },
        {
          id: 'thatched_hut',
          name: 'Thatched Hut',
          type: 'dwelling',
          description: 'The old man\'s home in the forest',
          importance: 4,
          tags: ['home', 'simple']
        },
        {
          id: 'forest',
          name: 'Forest',
          type: 'setting',
          description: 'Wooded area where the old man lives',
          importance: 4,
          tags: ['natural', 'sheltered']
        },
        {
          id: 'mountain',
          name: 'Mountain',
          type: 'landmark',
          description: 'High peaks that the rabbit must climb',
          importance: 6,
          tags: ['challenging', 'high']
        },
        {
          id: 'river',
          name: 'River',
          type: 'landmark',
          description: 'Flowing water that the rabbit must cross',
          importance: 5,
          tags: ['flowing', 'obstacle']
        }
      ],
      objects: [
        {
          id: 'sunshine',
          name: 'Sunshine',
          type: 'symbol',
          description: 'The warm light that represents freedom and joy',
          importance: 6,
          tags: ['warm', 'free']
        },
        {
          id: 'freedom',
          name: 'Freedom',
          type: 'virtue',
          description: 'The liberty that the rabbit enjoys in his daily life',
          importance: 7,
          tags: ['virtue', 'precious']
        },
        {
          id: 'curiosity',
          name: 'Curiosity',
          type: 'virtue',
          description: 'The rabbit\'s desire to learn about the moon',
          importance: 8,
          tags: ['virtue', 'driving']
        },
        {
          id: 'reverence',
          name: 'Reverence',
          type: 'virtue',
          description: 'The rabbit\'s deep respect for the moon',
          importance: 8,
          tags: ['virtue', 'respectful']
        },
        {
          id: 'medicine',
          name: 'Mountain Medicine',
          type: 'artifact',
          description: 'Healing herbs that the rabbit brings to help the old man\'s wife',
          importance: 6,
          tags: ['healing', 'helpful']
        },
        {
          id: 'blessing',
          name: 'Special Blessing',
          type: 'artifact',
          description: 'Magical gift from the old man that helps the rabbit reach the moon',
          importance: 8,
          tags: ['magical', 'gift']
        },
        {
          id: 'promise',
          name: 'Promise',
          type: 'virtue',
          description: 'The commitment to pursue dreams despite challenges',
          importance: 9,
          tags: ['virtue', 'commitment']
        },
        {
          id: 'sacrifice',
          name: 'Sacrifice',
          type: 'virtue',
          description: 'The willingness to give up comfort for dreams',
          importance: 9,
          tags: ['virtue', 'noble']
        }
      ]
    },
    relationships: [
      { from: 'white_rabbit', to: 'distant_planet', type: 'lives on', strength: 8 },
      { from: 'white_rabbit', to: 'fields', type: 'runs in', strength: 7 },
      { from: 'white_rabbit', to: 'sunshine', type: 'enjoys', strength: 6 },
      { from: 'white_rabbit', to: 'freedom', type: 'enjoys', strength: 7 },
      { from: 'white_rabbit', to: 'curiosity', type: 'possesses', strength: 8 },
      { from: 'white_rabbit', to: 'reverence', type: 'feels for', strength: 8 },
      { from: 'white_rabbit', to: 'moon', type: 'longs for', strength: 10 },
      { from: 'white_rabbit', to: 'moon', type: 'wants to reach', strength: 9 },
      { from: 'white_rabbit', to: 'mountain', type: 'climbs', strength: 7 },
      { from: 'white_rabbit', to: 'river', type: 'crosses', strength: 6 },
      { from: 'white_rabbit', to: 'old_man', type: 'meets', strength: 8 },
      { from: 'old_man', to: 'thatched_hut', type: 'lives in', strength: 8 },
      { from: 'old_man', to: 'forest', type: 'lives in', strength: 7 },
      { from: 'old_man', to: 'old_man_wife', type: 'cares for', strength: 9 },
      { from: 'white_rabbit', to: 'medicine', type: 'brings', strength: 7 },
      { from: 'medicine', to: 'old_man_wife', type: 'heals', strength: 8 },
      { from: 'old_man', to: 'blessing', type: 'gives', strength: 8 },
      { from: 'blessing', to: 'white_rabbit', type: 'helps', strength: 9 },
      { from: 'white_rabbit', to: 'moon', type: 'reaches', strength: 10 },
      { from: 'white_rabbit', to: 'promise', type: 'demonstrates', strength: 9 },
      { from: 'white_rabbit', to: 'sacrifice', type: 'shows', strength: 9 }
    ],
    analysis: {
      total_entities: 11,
      total_relationships: 20,
      main_themes: ['determination', 'sacrifice', 'dreams', 'helping others', 'curiosity'],
      narrative_arc: 'A determined rabbit overcomes obstacles to reach his dream of the moon, while helping others along the way',
      complexity_score: 7
    }
  },
  crystal_city_story: {
    entities: {
      characters: [
        {
          id: 'queen_seraphina',
          name: 'Queen Seraphina',
          type: 'protagonist',
          description: 'Wise and just ruler of the Crystal City who inherited the throne from her mother',
          importance: 9,
          tags: ['ruler', 'wise', 'just']
        },
        {
          id: 'princess_aurora',
          name: 'Princess Aurora',
          type: 'protagonist',
          description: 'The queen\'s determined daughter who saves the city',
          importance: 10,
          tags: ['heroine', 'determined', 'brave']
        },
        {
          id: 'master_zephyr',
          name: 'Master Zephyr',
          type: 'mentor',
          description: 'Brilliant inventor who created the Solar Collector',
          importance: 8,
          tags: ['inventor', 'brilliant', 'mentor']
        },
        {
          id: 'spark',
          name: 'Spark',
          type: 'supporting_character',
          description: 'Young inventor and loyal friend to Princess Aurora',
          importance: 7,
          tags: ['friend', 'inventor', 'loyal']
        },
        {
          id: 'master_luminary',
          name: 'Master Luminary',
          type: 'mentor',
          description: 'Wise old sage who helps guide the quest',
          importance: 7,
          tags: ['sage', 'wise', 'guide']
        },
        {
          id: 'crystal_weaver',
          name: 'Crystal Weaver',
          type: 'legendary_character',
          description: 'Queen Seraphina\'s legendary mother who founded the city',
          importance: 6,
          tags: ['legendary', 'founder']
        }
      ],
      places: [
        {
          id: 'crystal_city',
          name: 'Crystal City of Light',
          type: 'city',
          description: 'Magnificent city made entirely of crystal in the heart of a vast desert',
          importance: 10,
          tags: ['magnificent', 'crystal', 'threatened']
        },
        {
          id: 'vast_desert',
          name: 'Vast Desert',
          type: 'setting',
          description: 'Endless desert where the sun blazes like a furnace',
          importance: 7,
          tags: ['harsh', 'endless', 'hot']
        },
        {
          id: 'dark_sands',
          name: 'Dark Sands',
          type: 'threat',
          description: 'Mysterious force that consumes light and leaves only darkness',
          importance: 9,
          tags: ['dangerous', 'consuming', 'dark']
        },
        {
          id: 'treacherous_mountains',
          name: 'Treacherous Mountains',
          type: 'landmark',
          description: 'Dangerous peaks that must be crossed during the quest',
          importance: 6,
          tags: ['dangerous', 'challenging']
        },
        {
          id: 'raging_rivers',
          name: 'Raging Rivers',
          type: 'landmark',
          description: 'Fast-flowing waters that must be crossed',
          importance: 5,
          tags: ['dangerous', 'flowing']
        },
        {
          id: 'crystal_cave',
          name: 'Crystal Cave',
          type: 'landmark',
          description: 'Deep cave in the mountains where the Heart Crystal is hidden',
          importance: 8,
          tags: ['hidden', 'deep', 'magical']
        }
      ],
      objects: [
        {
          id: 'solar_collector',
          name: 'Solar Collector',
          type: 'artifact',
          description: 'Massive crystal structure that captures and stores the sun\'s energy',
          importance: 9,
          tags: ['powerful', 'technological', 'essential']
        },
        {
          id: 'heart_crystal',
          name: 'Heart Crystal',
          type: 'artifact',
          description: 'Legendary gem that can purify darkness and restore light',
          importance: 10,
          tags: ['legendary', 'powerful', 'magical']
        },
        {
          id: 'ancient_texts',
          name: 'Ancient Texts',
          type: 'knowledge',
          description: 'Old writings in the Great Library that contain vital information',
          importance: 7,
          tags: ['ancient', 'knowledge', 'vital']
        },
        {
          id: 'great_library',
          name: 'Great Library',
          type: 'landmark',
          description: 'Repository of knowledge where Princess Aurora studies',
          importance: 6,
          tags: ['knowledge', 'important']
        },
        {
          id: 'sunlight',
          name: 'Sunlight',
          type: 'power',
          description: 'The energy source that powers the crystal city',
          importance: 8,
          tags: ['powerful', 'essential', 'energy']
        },
        {
          id: 'hope',
          name: 'Hope',
          type: 'virtue',
          description: 'The belief that light can overcome darkness',
          importance: 9,
          tags: ['virtue', 'powerful', 'motivating']
        },
        {
          id: 'determination',
          name: 'Determination',
          type: 'virtue',
          description: 'The will to persevere despite challenges',
          importance: 8,
          tags: ['virtue', 'strong', 'persistent']
        },
        {
          id: 'friendship',
          name: 'Friendship',
          type: 'virtue',
          description: 'The bond between allies working together',
          importance: 7,
          tags: ['virtue', 'supportive', 'important']
        }
      ]
    },
    relationships: [
      { from: 'queen_seraphina', to: 'crystal_city', type: 'rules', strength: 9 },
      { from: 'queen_seraphina', to: 'crystal_weaver', type: 'inherits from', strength: 8 },
      { from: 'princess_aurora', to: 'queen_seraphina', type: 'daughter of', strength: 9 },
      { from: 'princess_aurora', to: 'crystal_city', type: 'saves', strength: 10 },
      { from: 'master_zephyr', to: 'solar_collector', type: 'created', strength: 9 },
      { from: 'solar_collector', to: 'crystal_city', type: 'powers', strength: 9 },
      { from: 'princess_aurora', to: 'ancient_texts', type: 'studies', strength: 7 },
      { from: 'ancient_texts', to: 'great_library', type: 'stored in', strength: 8 },
      { from: 'princess_aurora', to: 'spark', type: 'friend of', strength: 8 },
      { from: 'princess_aurora', to: 'master_luminary', type: 'guided by', strength: 7 },
      { from: 'princess_aurora', to: 'heart_crystal', type: 'finds', strength: 10 },
      { from: 'heart_crystal', to: 'dark_sands', type: 'defeats', strength: 10 },
      { from: 'dark_sands', to: 'crystal_city', type: 'threatens', strength: 9 },
      { from: 'crystal_city', to: 'vast_desert', type: 'located in', strength: 8 },
      { from: 'princess_aurora', to: 'treacherous_mountains', type: 'crosses', strength: 7 },
      { from: 'princess_aurora', to: 'raging_rivers', type: 'crosses', strength: 6 },
      { from: 'heart_crystal', to: 'crystal_cave', type: 'hidden in', strength: 8 },
      { from: 'sunlight', to: 'solar_collector', type: 'captured by', strength: 9 },
      { from: 'princess_aurora', to: 'hope', type: 'embodies', strength: 9 },
      { from: 'princess_aurora', to: 'determination', type: 'shows', strength: 8 },
      { from: 'princess_aurora', to: 'friendship', type: 'values', strength: 7 }
    ],
    analysis: {
      total_entities: 14,
      total_relationships: 20,
      main_themes: ['leadership', 'technology', 'courage', 'hope', 'friendship', 'perseverance'],
      narrative_arc: 'A determined princess leads a quest to save her magical city from darkness through courage and friendship',
      complexity_score: 9
    }
  }
};

// Helper function to get mock data by story ID
export const getMockDataByStoryId = (storyId) => {
  return mockApiResponses[storyId] || null;
};

// Helper function to get all available story IDs
export const getAvailableStoryIds = () => {
  return Object.keys(mockApiResponses);
};

// Helper function to get story sample by ID
export const getStorySampleById = (storyId) => {
  return knowledgeGraphSamples.find(sample => sample.id === storyId) || null;
}; 